import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Form,
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { createFinanceManager } from "@/Calls/callDealer";

/* --------------------------------------------
   Schema & Types
-------------------------------------------- */
const FinanceManagerSchema = z.object({
    financemanager_firstname: z.string().min(2, "First name required"),
    financemanager_lastname: z.string().min(2, "Last name required"),
    financemanager_email: z.string().email("Invalid email"),
    financemanager_password: z.string().min(6, "Password must be at least 6 chars"),
    financemanager_mobilephone_number: z.string().optional(),
    financemanager_job_title: z.string().optional(),
});

type FinanceManagerForm = z.infer<typeof FinanceManagerSchema>;

/* --------------------------------------------
   Component
-------------------------------------------- */
export default function FinanceManagerCreatePage() {
    const form = useForm<FinanceManagerForm>({
        resolver: zodResolver(FinanceManagerSchema),
        defaultValues: {
            financemanager_firstname: "",
            financemanager_lastname: "",
            financemanager_email: "",
            financemanager_password: "",
            financemanager_mobilephone_number: "",
            financemanager_job_title: "",
        },
    });

    const [submitting, setSubmitting] = React.useState(false);

    async function onSubmit(values: FinanceManagerForm) {
        try {
            setSubmitting(true);
            await createFinanceManager(values);

            toast("✅ Finance Manager created", {
                description: `${values.financemanager_firstname} ${values.financemanager_lastname} has been added successfully.`,
                action: {
                    label: "View Staff",
                    onClick: () => window.location.assign("/dealer/staff"),
                },
            });

            form.reset();
        } catch (err) {
            console.error(err);
            const message =
                err instanceof Error ? err.message : "Unexpected error — please try again later.";
            toast("❌ Error creating Finance Manager", { description: message });
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="container mx-auto max-w-2xl py-8">
            <Card>
                <CardHeader>
                    <CardTitle>Add New Finance Manager</CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="financemanager_firstname"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>First Name</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="John" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="financemanager_lastname"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Last Name</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="Doe" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="financemanager_email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Email</FormLabel>
                                        <FormControl>
                                            <Input {...field} type="email" placeholder="email@dealer.com" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="financemanager_password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Password</FormLabel>
                                        <FormControl>
                                            <Input {...field} type="password" placeholder="********" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="financemanager_mobilephone_number"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Mobile Phone</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="(555) 555-5555" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="financemanager_job_title"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Job Title</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="Finance Manager" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button type="submit" disabled={submitting} className="w-full">
                                {submitting ? "Creating..." : "Create Finance Manager"}
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
}
